// What the UI still needs from the recent-buyers work: whether an audience is
// SAM-managed, and the neutral line for a held refresh.
//
// Owner directive (2026-09-27): the UI shows each destination's total rows
// uploaded and nothing about recent buyers — no count beside a lookalike, no
// split of an upload by stream, no word on how they are delivered. The
// journal's per-stream counts are withheld at the API response boundary.

import type { RecentBuyersNoticeKind } from '@/api/types.gen';
import { fmtDateTime, withEt } from '@/lib/datetime';

/** Mirrors `shared::RECENT_BUYERS_MANAGED_BY`. */
export const RECENT_BUYERS_MANAGED_BY = 'sam:recent_buyers';

type ManagedAudience = {
  id?: string;
  managed_by?: string | null;
  archived?: boolean;
  estimated_size?: number | null;
  audience_type?: string;
};

/** Is this row owned by a reconciler rather than by an operator? */
export function isManagedAudience(audience?: ManagedAudience | null): boolean {
  return Boolean(audience?.managed_by);
}

/**
 * `RecentBuyersNotice` as it arrives in the browser: `since_ms` is an `i64`,
 * which ts-rs types as a `bigint`, but JSON carries it as a plain number.
 */
export type RecentBuyersNoticeWire = {
  kind: RecentBuyersNoticeKind | string;
  since_ms: number | bigint;
};

/** What a destination row shows for a notice. */
export type RecentBuyersNoticeLine = {
  kind: RecentBuyersNoticeKind;
  text: string;
};

// The kinds batch-worker writes, keyed so a kind added to the generated union
// without an entry here fails typecheck. Each is a lookalike refresh it is
// holding and will retry on its own; the drawer says exactly that and no more
// (owner directive, 2026-09-27): no line names what the refresh waits for, and
// none carries a hover.
const NOTICE_KINDS: Record<RecentBuyersNoticeKind, true> = {
  held_companion_unavailable: true,
  held_writes_paused: true,
  waiting_for_delivery_ack: true,
  delivery_ack_overdue: true,
};

/** The one sentence every notice reads as. */
export const HELD_REFRESH_NOTICE = 'Refresh held; retrying automatically';

/**
 * The line for batch-worker's notice on one destination, or `null` when there
 * is nothing to say.
 *
 * **Display only.** The notice rides the leg's journal to say a refresh is
 * held, which the leg's own status does not — a held refresh leaves it reading
 * synced. Nothing in the UI may gate on it. An absent notice, and a kind this
 * build does not know (a newer worker wrote it), render nothing rather than a
 * guess.
 */
export function recentBuyersNoticeLine(
  notice?: RecentBuyersNoticeWire | null,
): RecentBuyersNoticeLine | null {
  if (!notice || !Object.prototype.hasOwnProperty.call(NOTICE_KINDS, notice.kind)) return null;
  const since = Number(notice.since_ms);
  const when = Number.isFinite(since) && since > 0 ? ` (since ${withEt(fmtDateTime(since))})` : '';
  return { kind: notice.kind as RecentBuyersNoticeKind, text: `${HELD_REFRESH_NOTICE}${when}` };
}
